// aufzaehlungText.ts — Aufzählungen in einem gewöhnlichen Textfeld.
//
// Die Tätigkeitsbeschreibung einer Firma ist Fließtext mit Gliederung: „was
// macht der Betrieb", darunter die einzelnen Sparten, darunter deren Details.
// Gespeichert wird dafür bewusst REINER TEXT mit Zeilenumbrüchen und
// Einrückung — kein HTML. Der Text steht auch außerhalb der Maske (Suche,
// Export, Beruf-Sektion verbundener Personen); HTML müsste dort überall
// gerendert oder entfernt werden, Klartext nirgends.
//
// Dieses Modul ist die reine Rechenschicht dahinter: Es kennt nur Text und
// Cursorpositionen, kein DOM und kein React — und ist damit vollständig
// unit-testbar. Die Anbindung an ein <textarea> macht `useAufzaehlung`.

/** Das Aufzählungszeichen, mit dem eine Punktzeile beginnt. */
export const BULLET = '•';

/**
 * Eine Einrückungsebene = zwei Leerzeichen — genau die Breite von „• ".
 * Ein Unterpunkt beginnt damit exakt unter dem TEXT seines Oberpunkts
 * (hängende Einrückung), was die Verschachtelung auch im reinen Text lesbar
 * hält, wo kein Renderer sie nachzeichnet.
 */
export const EINRUECKUNG = '  ';

/** Text plus Auswahl — Ein- und Ausgabe jeder Operation hier. */
export interface TextZustand {
  text: string;
  start: number;
  end: number;
}

/** Eine Zeile, zerlegt in Einzug, Aufzählungszeichen und Inhalt. */
export interface ZeilenTeile {
  /** Führende Leerzeichen vor dem Aufzählungszeichen. */
  einzug: string;
  /** Steht am Zeilenanfang ein Aufzählungszeichen? */
  bullet: boolean;
  /** Was hinter Einzug und Aufzählungszeichen steht. */
  inhalt: string;
}

/** Zerlegt eine einzelne Zeile. Erkennt „•", „-" und „*" als Aufzählung. */
export function zeileZerlegen(zeile: string): ZeilenTeile {
  const m = /^([ \t]*)([•*-])[ \t]+(.*)$/.exec(zeile);
  if (m) return { einzug: m[1], bullet: true, inhalt: m[3] };
  const nur = /^([ \t]*)([•*-])[ \t]*$/.exec(zeile);
  if (nur) return { einzug: nur[1], bullet: true, inhalt: '' };
  const e = /^([ \t]*)(.*)$/.exec(zeile) as RegExpExecArray;
  return { einzug: e[1], bullet: false, inhalt: e[2] };
}

/** Setzt eine zerlegte Zeile wieder zusammen. */
export function zeileBauen(t: ZeilenTeile): string {
  if (!t.bullet) return t.einzug + t.inhalt;
  // Eine Punktzeile ohne Inhalt behält kein Leerzeichen am Ende — sonst
  // sammelt der gespeicherte Text unsichtbaren Müll an.
  return t.inhalt ? `${t.einzug}${BULLET} ${t.inhalt}` : `${t.einzug}${BULLET}`;
}

/** Index der Zeile, in der `offset` liegt (0-basiert). */
function zeileVonOffset(zeilen: string[], offset: number): number {
  let pos = 0;
  for (let i = 0; i < zeilen.length; i++) {
    // +1 für den Zeilenumbruch; `<=` weil ein Cursor am Zeilenende noch zu
    // dieser Zeile gehört, nicht zur nächsten.
    if (offset <= pos + zeilen[i].length) return i;
    pos += zeilen[i].length + 1;
  }
  return Math.max(0, zeilen.length - 1);
}

/** Startoffset jeder Zeile im Gesamttext. */
function zeilenStarts(zeilen: string[]): number[] {
  const out: number[] = [];
  let pos = 0;
  for (const z of zeilen) {
    out.push(pos);
    pos += z.length + 1;
  }
  return out;
}

/**
 * Wendet `fn` auf alle von der Auswahl berührten Zeilen an und rechnet die
 * Cursorpositionen mit. Alle Operationen hier ändern ausschließlich den
 * Zeilenanfang (Einzug/Aufzählungszeichen), deshalb genügt es, jeden Offset um
 * die Längenänderung seiner eigenen und aller vorherigen Zeilen zu verschieben.
 */
function zeilenAbbilden(
  z: TextZustand,
  fn: (zeile: string, index: number) => string,
): TextZustand {
  const zeilen = z.text.split('\n');
  const starts = zeilenStarts(zeilen);
  const von = zeileVonOffset(zeilen, z.start);
  const bis = zeileVonOffset(zeilen, z.end);

  const neu = [...zeilen];
  const deltas = new Array(zeilen.length).fill(0);
  for (let i = von; i <= bis; i++) {
    neu[i] = fn(zeilen[i], i);
    deltas[i] = neu[i].length - zeilen[i].length;
  }

  const verschieben = (offset: number): number => {
    const zi = zeileVonOffset(zeilen, offset);
    const vorher = deltas.slice(0, zi).reduce((a, b) => a + b, 0);
    const inZeile = offset - starts[zi];
    // Der Cursor darf nicht in das Präfix rutschen, das gerade entfernt wurde:
    // die Position innerhalb der Zeile wird bei Bedarf auf 0 geklemmt.
    const neuInZeile = Math.max(0, Math.min(inZeile + deltas[zi], neu[zi].length));
    return starts[zi] + vorher + neuInZeile;
  };

  return { text: neu.join('\n'), start: verschieben(z.start), end: verschieben(z.end) };
}

/** Liegt in der Auswahl mindestens eine Aufzählungszeile? */
export function hatAufzaehlung(z: TextZustand): boolean {
  const zeilen = z.text.split('\n');
  const von = zeileVonOffset(zeilen, z.start);
  const bis = zeileVonOffset(zeilen, z.end);
  for (let i = von; i <= bis; i++) if (zeileZerlegen(zeilen[i]).bullet) return true;
  return false;
}

/**
 * Aufzählung an/aus für die berührten Zeilen. Solange noch eine Zeile ohne
 * Punkt dabei ist, bekommen ALLE einen — erst wenn durchgehend Punkte stehen,
 * nimmt derselbe Knopf sie wieder weg. Das ist das Verhalten, das man von
 * Textverarbeitungen kennt.
 */
export function aufzaehlungUmschalten(z: TextZustand): TextZustand {
  const zeilen = z.text.split('\n');
  const von = zeileVonOffset(zeilen, z.start);
  const bis = zeileVonOffset(zeilen, z.end);

  let alleBullet = true;
  for (let i = von; i <= bis; i++) {
    const t = zeileZerlegen(zeilen[i]);
    // Leerzeilen zählen nicht mit: sonst verhindert eine einzelne Leerzeile
    // im Block, dass der Knopf die Punkte wieder entfernt.
    if (!t.bullet && zeilen[i].trim() !== '') alleBullet = false;
  }

  return zeilenAbbilden(z, (zeile) => {
    const t = zeileZerlegen(zeile);
    if (alleBullet) return t.bullet ? zeileBauen({ ...t, bullet: false }) : zeile;
    if (t.bullet || zeile.trim() === '') return zeile;
    return zeileBauen({ ...t, bullet: true });
  });
}

/** Eine Ebene tiefer. */
export function einruecken(z: TextZustand): TextZustand {
  return zeilenAbbilden(z, (zeile) => (zeile.trim() === '' ? zeile : EINRUECKUNG + zeile));
}

/** Eine Ebene zurück — entfernt so viel Einzug, wie tatsächlich da ist. */
export function ausruecken(z: TextZustand): TextZustand {
  return zeilenAbbilden(z, (zeile) => {
    const t = zeileZerlegen(zeile);
    if (t.einzug.length === 0) return zeile;
    const kuerzer = t.einzug.slice(0, Math.max(0, t.einzug.length - EINRUECKUNG.length));
    return zeileBauen({ ...t, einzug: kuerzer });
  });
}

/**
 * Enter in einer Aufzählungszeile: setzt den nächsten Punkt auf derselben
 * Ebene fort. Steht der Cursor in einem LEEREN Punkt, endet die Aufzählung
 * stattdessen — erst eine Ebene zurück, auf Ebene 0 dann ganz. So kommt man
 * ohne Maus wieder aus der Liste heraus, wie in jedem Editor.
 *
 * Gibt `null` zurück, wenn nichts zu tun ist — dann soll das Textfeld sein
 * normales Enter behalten.
 */
export function enterFortsetzen(z: TextZustand): TextZustand | null {
  if (z.start !== z.end) return null;
  const zeilen = z.text.split('\n');
  const starts = zeilenStarts(zeilen);
  const zi = zeileVonOffset(zeilen, z.start);
  const t = zeileZerlegen(zeilen[zi]);
  if (!t.bullet) return null;

  if (t.inhalt.trim() === '') {
    // Leerer Punkt → Liste verlassen (schrittweise, Ebene für Ebene).
    //
    // Das Leerzeichen hinter dem Punkt wird hier bewusst gesetzt (statt über
    // `zeileBauen`, das eine leere Punktzeile ohne Rand baut): Der Cursor landet
    // direkt dahinter und der nächste Tastendruck ist Text. Ohne das Leerzeichen
    // schriebe der Nutzer „•Abzüge" statt „• Abzüge".
    const neueZeile =
      t.einzug.length > 0
        ? `${t.einzug.slice(0, Math.max(0, t.einzug.length - EINRUECKUNG.length))}${BULLET} `
        : '';
    const neu = [...zeilen];
    neu[zi] = neueZeile;
    return {
      text: neu.join('\n'),
      start: starts[zi] + neueZeile.length,
      end: starts[zi] + neueZeile.length,
    };
  }

  const einfuegen = `\n${t.einzug}${BULLET} `;
  const text = z.text.slice(0, z.start) + einfuegen + z.text.slice(z.end);
  const cursor = z.start + einfuegen.length;
  return { text, start: cursor, end: cursor };
}
